import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authApi from '../services/api/authApi';
import { useNotification } from '../hooks/useNotification';
import { useLoader } from './loaderContext';
import { NotificationContext } from './notificationContext';

const AuthContext = createContext(null);


export const AuthProvider = ({ children }) => {
  const { showLoader, hideLoader } = useLoader();
  const { refreshNotifications, fetchChatHistory } = useContext(NotificationContext);
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [authState, setAuthState] = useState({
    isAuthenticated: !!localStorage.getItem('accessToken'),
    isPending2FA: false,
  });
  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    const tempAuth = sessionStorage.getItem('tempAuth');

    setAuthState(prev => ({
      ...prev,
      isAuthenticated: !!token,
      isPending2FA: !!tempAuth
    }));
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  const handleAuth = useCallback(async (action, data) => {
    showLoader();
    try {
      const response = await authApi[action](data);
      return response;

    } catch (error) {
      showError(error);
    } finally {
      hideLoader();
    }
  }, [showLoader, hideLoader, showError]);


  const login = useCallback(async (credentials) => {
    const response = await handleAuth('Login', credentials);
    if (response.status) {
      sessionStorage.setItem('tempAuth', JSON.stringify(credentials));
      setAuthState(prev => ({ ...prev, isPending2FA: true }));
      navigate('/twoFactorLogin');
    }
  }, [handleAuth, navigate]);

  const getOrCreateDeviceId = () => {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = Math.random().toString(36).substring(2, 15);
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  const verify2FA = useCallback(async (code) => {
    const email = sessionStorage.getItem('email');
    const password = sessionStorage.getItem('password');
    const deviceId = getOrCreateDeviceId();
    const response = await handleAuth('TwoFactorLogin', {
      email: email,
      password: password,
      token: code,
      deviceId: deviceId,
      userAgent: navigator.userAgent,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      pixelRatio: window.devicePixelRatio
    });


    if (response.status) {
      localStorage.setItem('accessToken', response.resultData.token.token);
      sessionStorage.clear();
      setAuthState(prev => ({
        ...prev,
        isAuthenticated: true,
        isPending2FA: false
      }));
      navigate('/dashboard');
      refreshNotifications();
      fetchChatHistory();
    }
  }, [handleAuth, navigate]);

  const logout = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    setAuthState({
      isAuthenticated: false,
      isPending2FA: false,
      isLoading: false
    });
    navigate('/');
  }, [navigate]);

  return (
    <AuthContext.Provider value={{
      ...authState,
      setAuthState,
      login,
      verify2FA,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};